// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import { Card, notification, Flex, Typography, Tabs } from "antd";
import moment from "moment";
import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useRecoilValue } from "recoil";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate, useParams } from "react-router";
// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------
import {
	updateSelectedMuxVideoUrl,
	updateSelectedWorkout,
	updateMembersWorkoutsSelectedCategories,
	updateMembersWorkoutsSelectedEquipment,
	updateMembersWorkoutsSelectedTrainers,
	updateMembersWorkoutsSelectedMusic,
	updateMembersWorkoutsSelectedDuration,
} from "store/slices";
import {
	useCreateCustomerMutation,
	useStripeFetchTransfersMutation,
	useStripeGetCustomerConnectedExternalAccountsMutation,
	useStripeGetGymSubscriptionQuery,
	useGetDomainGymQuery,
	useGetGymMembersByDateQuery,
	useGetGymMonthlyActiveUsersQuery,
	useGetGymWorkoutsQuery,
	useGetScheduledStreamsQuery,
	useGetStatsForGymQuery,
	useStripeGetGymProductQuery,
} from "store/api/api";

// -----------------------------------------------------------------------------
// Components and helpers
// -----------------------------------------------------------------------------
import { CreateWorkoutModal } from "components/Modals/CreateWorkoutModal";
import { EditWorkoutModal } from "components/Modals/EditWorkoutModal";
import { ScheduleLiveModal } from "components/Modals/ScheduleLiveModal";
import { GoLiveModal } from "components/Modals/GoLiveModal";
import WorkoutsTable from "components/Workouts/WorkoutsTable";
import { userAtom } from "../../../globalAtoms";
import { UserEditIcon } from "components/Icons/UserEditIcon";
import { ChevronRightIcon } from "components/Icons";
import { LoadingMask } from "components/Common/LoadingMask";
import InviteBanner from "components/UI/InviteBanner";
import "./styles.scss";
import alpha from "helpers/alpha";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import StripeAlerts, { PhoneNumberAlert, StripeSubscriptionAlerts } from "components/Alerts";
import { isUserMember } from "constants/ui";
import { workoutTypes } from "helpers/constants";
import NeedHelpComponent from "../LiveStream/NeedHelp";
import StartingSoon from "../LiveStream/StartingSoon";

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const TabLabels = ({ children, active }) => (
	<Typography.Text style={{ color: active ? "#000" : "rgba(0,0,0,0.5)" }} className="tab-label" strong>
		{children}
	</Typography.Text>
);

const Home = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const user = useRecoilValue(userAtom);
	const [showCreateWorkoutModal, setShowCreateWorkoutModal] = useState(false);
	const [showEditWorkoutModal, setShowEditWorkoutModal] = useState(false);
	const [selectedLiveStream, setSelectedLiveStream] = useState(null);
	const [isEditingLive, setIsEditingLive] = useState(false);
	const [showScheduleLiveModal, setShowScheduleLiveModal] = useState(false);
	const [showGoLiveModal, setShowGoLiveModal] = useState(false);
	const [transfers, setTransfers] = useState(null);
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse?.data?.id;
	const isUsedSBPayments = !!(domainGymResponse?.data?.is_used_sb_payments ?? 1);
	const [dataCount, setDataCount] = useState({});

	const workoutsResponse = useGetGymWorkoutsQuery({ gym_id: domainGymId }, { skip: !domainGymResponse.data });
	const [createStripeCustomerTrigger, createCustomerObj] = useCreateCustomerMutation();
	const getGymProductResponse = useStripeGetGymProductQuery({ gymId: domainGymId }, { skip: !domainGymId });
	const [getConnectedAccountsTrigger] = useStripeGetCustomerConnectedExternalAccountsMutation();
	const [fetchTransfersTrigger] = useStripeFetchTransfersMutation();
	const { data: statsData } = useGetStatsForGymQuery(
		{ gym_id: domainGymId, start_date: "", end_date: "" },
		{ skip: !domainGymResponse.data }
	);

	const getGymSubscriptionResponse = useStripeGetGymSubscriptionQuery({ gymId: domainGymId }, { skip: !domainGymId });
	const userIsSubscribed = getGymSubscriptionResponse?.data?.subscription;

	const { data: gymMembersData } = useGetGymMembersByDateQuery({});
	const { data: gymMonthlyActiveUsers } = useGetGymMonthlyActiveUsersQuery();

	const [userHasPlan, setUserHasPlan] = useState(undefined);
	const [userPayoutsEnabled, setUserPayoutsEnabled] = useState(undefined);
	const [connectedAccountId, setConnectedAccountId] = useState(null);

	const showcard = useMemo(
		() => isUsedSBPayments && (!userIsSubscribed || !userHasPlan || !userPayoutsEnabled),
		[isUsedSBPayments, userIsSubscribed, userHasPlan, userPayoutsEnabled]
	);
	const loading = useMemo(
		() => userIsSubscribed === undefined || userHasPlan === undefined || userPayoutsEnabled === undefined,
		[userIsSubscribed, userHasPlan, userPayoutsEnabled]
	);

	const fetchCustomerConnectedExternalAccounts = async () => {
		try {
			const bankAccountsRes = await getConnectedAccountsTrigger({ account_id: connectedAccountId }).unwrap();
			setUserPayoutsEnabled(bankAccountsRes?.data?.length > 0);
		} catch (error) {
			setUserPayoutsEnabled(false);
		}
	};

	useEffect(() => {
		if (getGymProductResponse.isLoading) {
			setUserHasPlan(undefined);
		} else if (getGymProductResponse.isError || !getGymProductResponse?.data?.id) {
			setUserHasPlan(false);
		} else if (getGymProductResponse.isSuccess) {
			setUserHasPlan(true);
		}
	}, [getGymProductResponse]);

	useEffect(() => {
		if (user?.gym?.stripe_info?.stripe_connected_account_id) {
			setConnectedAccountId(user.gym.stripe_info.stripe_connected_account_id);
		}
	}, [user]);

	useEffect(() => {
		if (connectedAccountId) {
			fetchCustomerConnectedExternalAccounts();
		}
	}, [connectedAccountId]);

	const fetchTransfers = async () => {
		try {
			const transfersResponse = await fetchTransfersTrigger({
				account_id: connectedAccountId,
			}).unwrap();
			setTransfers(transfersResponse || 0);
		} catch (error) {}
	};

	const addStripeCustomerId = async () => {
		try {
			await createStripeCustomerTrigger().unwrap();
		} catch (error) {}
	};

	useEffect(() => {
		if (createCustomerObj?.isSuccess) {
			notification.success({
				message: "Customer Created",
			});
		} else if (createCustomerObj.isError) {
			notification.error({
				message: "Error Creating Customer",
			});
		}
	}, [createCustomerObj]);

	useEffect(() => {
		if (user && !user?.customer_id) {
			addStripeCustomerId();
		}
	}, [user]);

	useEffect(() => {
		if (!transfers && typeof transfers !== "number" && connectedAccountId) {
			fetchTransfers();
		}
	}, [transfers, connectedAccountId]);

	useEffect(() => {
		return () => {
			dispatch(updateMembersWorkoutsSelectedEquipment([]));
			dispatch(updateMembersWorkoutsSelectedTrainers([]));
			dispatch(updateMembersWorkoutsSelectedMusic([]));
			dispatch(updateMembersWorkoutsSelectedDuration([]));
			dispatch(updateMembersWorkoutsSelectedCategories([0]));
		};
	}, []);

	const renderOverviewCard = (title, value, timeValue, percent) => {
		return (
			<Card hoverable className="sb-creator-home__section__card" data-id="lblHomeOverviewCard">
				<p className="sb-creator-home__section__card__title" data-id="lblHomeOverviewCardTitle">
					{title}
				</p>
				<p className="sb-creator-home__section__card__value" data-id="lblHomeOverviewCardValue">
					{value}
				</p>
			</Card>
		);
	};

	const getUserData = () => {
		const users = gymMembersData?.subscribers?.filter((u) => isUserMember(u?.association_type, u?.subscription_status));
		const lastMonthDate = moment().subtract(1, "months");
		const lastMonth = users?.filter((u) => lastMonthDate.isBefore(moment(u?.last_login_at)));
		return {
			total: users?.length || 0,
			lastMonth: lastMonth?.length || 0,
			percent: users?.length > 0 ? ((lastMonth?.length / users?.length) * 100).toFixed(1) : 0,
		};
	};

	const userData = getUserData();

	const onEditSetVisible = (bool) => {
		setShowEditWorkoutModal(bool);
		if (!bool) {
			dispatch(updateSelectedWorkout({ workout: null }));
			dispatch(updateSelectedMuxVideoUrl({ url: null }));
		}
	};

	const CheckRow = ({ text, fulfilled, navigationPath, num }) => {
		if (fulfilled) {
			return null;
		}
		return (
			<div
				className="sb-creator-home__section__banner__setup-guide__checklist__checkrow"
				style={{ justifyContent: "space-between" }}
			>
				<h1 className="sb-creator-home__section__banner__setup-guide__checklist__checkrow__text__incomplete">
					<div
						style={{
							height: "24px",
							width: "24px",
							borderRadius: "50%",
							color: primaryColor,

							border: `1px solid ${primaryColor}`,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",

							textAlign: "center",
						}}
					>
						<p style={{ marginBottom: 0 }}>{num}</p>
					</div>
					{text}
				</h1>

				<div
					className="sb-creator-home__section__banner__setup-guide__checklist__checkrow__test"
					onClick={() => navigate(navigationPath)}
				>
					<ChevronRightIcon style={{ height: "16px" }} />
				</div>
			</div>
		);
	};

	const bannerProps = [
		{
			text: "Select Pricing Plan",
			fulfilled: userIsSubscribed,
			navigationPath: "pricing-plan?currentPage=sweatbase",
		},
		{
			text: "Create Member Plan",
			fulfilled: userHasPlan,
			navigationPath: "pricing-plan?currentPage=member",
		},
		{
			text: "Setup Earnings Account",
			fulfilled: userPayoutsEnabled,
			navigationPath: "pricing-plan?currentPage=earnings",
		},
	]
		.filter((i) => !i.fulfilled)
		.map((item, i) => ({ ...item, num: i + 1 }));

	const primaryColor = usePrimaryColor();
	const [activeTab, setActiveTab] = useState("all");

	return (
		<div
			style={{
				"--background": alpha(primaryColor, 0.06),
				"--primary-color": primaryColor,
				"--circle-background": alpha(primaryColor, 0.1),
			}}
			className="sb-creator-home"
		>
			<div className="sb-creator-home__section" data-id="sectionHomeOverview">
				<PhoneNumberAlert />
				<StripeSubscriptionAlerts />
				<InviteBanner />
				{showcard && (
					<div className="sb-creator-home__section__banner">
						{loading ? (
							<div className="sb-creator-home__section__banner__loading">
								<LoadingMask />
							</div>
						) : (
							<>
								<div className="sb-creator-home__section__banner__edit-circle">
									<UserEditIcon fill={primaryColor} />
								</div>
								<div className="sb-creator-home__section__banner__text-container">
									<h1 className="sb-creator-home__section__banner__text-container__title">
										Let's Finish setting up your account
									</h1>
									<h1 className="sb-creator-home__section__banner__text-container__subtitle">
										In order to start receiving members and payments, finish setting up your member plan and payment
										settings.
									</h1>
									<h1 className="sb-creator-home__section__banner__text-container__notice">
										* it may take up to 24 hours for your account to be approved and connected
									</h1>
								</div>
								<div className="sb-creator-home__section__banner__setup-guide">
									<h1 className="sb-creator-home__section__banner__setup-guide__title">Setup Guide</h1>
									{bannerProps.map((item) => (
										<CheckRow key={item.text} {...item} />
									))}
								</div>
							</>
						)}
					</div>
				)}
				<div
					className="starting-soon"
					style={{ "--primary-color": "#F95C25", "--background-color": "#FFDFC9" }}
					data-id="lblStartingSoon"
				>
					<div className="sb-creator-home__section__head">
						<div className="sb-creator-home__section__head__title-container">
							<h1 className="sb-creator-home__section__head__title-container__title">Starting Soon</h1>
							<NeedHelpComponent style={{ flex: "unset", minWidth: "unset" }} />
						</div>
					</div>
				</div>
				<StartingSoon />
				<StripeAlerts />

				<div className="sb-creator-home__section__head">
					<div className="sb-creator-home__section__head__title-container">
						<h1 className="sb-creator-home__section__head__title-container__title">Overview</h1>
					</div>
				</div>
				{renderOverviewCard("EARNINGS", `$ ${(transfers / 100 || 0).toFixed(2)}` || 0, "$0 last month", 0)}
				{renderOverviewCard("TOTAL MEMBERS", userData.total, `${userData.lastMonth} last month`, userData.percent)}
				{renderOverviewCard(
					"WORKOUTS STARTED",
					statsData?.reduce((acc, stat) => {
						return stat.total_views + acc;
					}, 0) || 0,
					"0 last month",
					0
				)}
				{renderOverviewCard(
					"Monthly Active Users",
					gymMonthlyActiveUsers?.monthly_active_users || 0,
					`${userData?.lastMonth || 0} last month`,
					userData?.percent
				)}
				{renderOverviewCard(
					"Song Plays",
					statsData?.reduce((acc, stat) => {
						return stat.total_song_plays + acc;
					}, 0) || 0,
					"0 last month",
					0
				)}
			</div>
			<div className="sb-creator-home__section" data-id="sb-creator-home-workouts">
				<div className="sb-creator-home__section__head" data-id="homeWorkoutsHead">
					<div className="sb-creator-home__section__head__title-container">
						<h1 className="sb-creator-home__section__head__title-container__title">Workout Library</h1>
						<div className="sb-creator-home__section__head__title-container__count" data-id="lblHomeWorkoutsCount">
							<label>{dataCount[activeTab] || 0}</label>
						</div>
					</div>
				</div>
				<div className="sb-creator-home__section__table" data-id="homeWorkoutsTableContainer">
					<Tabs
						onChange={setActiveTab}
						items={[
							{
								label: <TabLabels active={activeTab === "all"}>All</TabLabels>,
								key: "all",
								children: (
									<WorkoutsTable
										setShowEditWorkoutModal={setShowEditWorkoutModal}
										setIsEditingLive={setIsEditingLive}
										setSelectedLiveStream={setSelectedLiveStream}
										workoutType={Object.values(workoutTypes)}
										hideBulkStatusChange
										updateCount={(count) => {
											setDataCount((prev) => ({ ...prev, all: count }));
										}}
									/>
								),
							},
							{
								label: <TabLabels active={activeTab === workoutTypes.ON_DEMAND}>On Demand</TabLabels>,
								key: workoutTypes.ON_DEMAND,
								children: (
									<WorkoutsTable
										setShowEditWorkoutModal={setShowEditWorkoutModal}
										setIsEditingLive={setIsEditingLive}
										setSelectedLiveStream={setSelectedLiveStream}
										workoutType={[workoutTypes.ON_DEMAND]}
										updateCount={(count) => {
											setDataCount((prev) => ({ ...prev, [workoutTypes.ON_DEMAND]: count }));
										}}
									/>
								),
							},
							{
								label: (
									<TabLabels active={activeTab === workoutTypes.SCHEDULED_ON_DEMAND}>Scheduled On Demand</TabLabels>
								),
								key: workoutTypes.SCHEDULED_ON_DEMAND,
								children: (
									<WorkoutsTable
										setShowEditWorkoutModal={setShowEditWorkoutModal}
										setIsEditingLive={setIsEditingLive}
										setSelectedLiveStream={setSelectedLiveStream}
										workoutType={[workoutTypes.SCHEDULED_ON_DEMAND]}
										hideBulkStatusChange
										updateCount={(count) => {
											setDataCount((prev) => ({ ...prev, [workoutTypes.SCHEDULED_ON_DEMAND]: count }));
										}}
									/>
								),
							},
							{
								label: <TabLabels active={activeTab === workoutTypes.CURRENTLY_LIVE}>Currently Live</TabLabels>,
								key: workoutTypes.CURRENTLY_LIVE,
								children: (
									<WorkoutsTable
										setShowEditWorkoutModal={setShowEditWorkoutModal}
										setIsEditingLive={setIsEditingLive}
										setSelectedLiveStream={setSelectedLiveStream}
										workoutType={[workoutTypes.CURRENTLY_LIVE]}
										hideBulkStatusChange
										updateCount={(count) => {
											setDataCount((prev) => ({ ...prev, [workoutTypes.CURRENTLY_LIVE]: count }));
										}}
									/>
								),
							},
							{
								label: <TabLabels active={activeTab === workoutTypes.SCHEDULED_LIVE_STREAM}>Scheduled Live</TabLabels>,
								key: workoutTypes.SCHEDULED_LIVE_STREAM,
								children: (
									<WorkoutsTable
										setShowEditWorkoutModal={setShowEditWorkoutModal}
										setIsEditingLive={setIsEditingLive}
										setSelectedLiveStream={setSelectedLiveStream}
										workoutType={[workoutTypes.SCHEDULED_LIVE_STREAM]}
										hideBulkStatusChange
										updateCount={(count) => {
											setDataCount((prev) => ({ ...prev, [workoutTypes.SCHEDULED_LIVE_STREAM]: count }));
										}}
									/>
								),
							},
							{
								label: (
									<TabLabels active={activeTab === workoutTypes.SCHEDULED_PRE_RECORDED}>
										Scheduled Pre Recorded
									</TabLabels>
								),
								key: workoutTypes.SCHEDULED_PRE_RECORDED,
								children: (
									<WorkoutsTable
										setShowEditWorkoutModal={setShowEditWorkoutModal}
										setIsEditingLive={setIsEditingLive}
										setSelectedLiveStream={setSelectedLiveStream}
										workoutType={[workoutTypes.SCHEDULED_PRE_RECORDED]}
										hideBulkStatusChange
										updateCount={(count) => {
											setDataCount((prev) => ({ ...prev, [workoutTypes.SCHEDULED_PRE_RECORDED]: count }));
										}}
									/>
								),
							},
						]}
						data-id="homeWorkoutsTabs"
					/>
				</div>
			</div>
			<CreateWorkoutModal
				data-id="CreateWorkoutModal"
				open={showCreateWorkoutModal}
				setOpen={setShowCreateWorkoutModal}
			/>
			<EditWorkoutModal
				open={showEditWorkoutModal}
				setOpen={onEditSetVisible}
				isEditingLive={isEditingLive}
				setIsEditingLive={setIsEditingLive}
				liveStream={selectedLiveStream}
				onClose={() => workoutsResponse?.refetch?.()}
			/>
			<ScheduleLiveModal open={showScheduleLiveModal} setOpen={setShowScheduleLiveModal} />
			<GoLiveModal open={showGoLiveModal} setOpen={setShowGoLiveModal} liveStream={selectedLiveStream} />
		</div>
	);
};

export default Home;
